import type { Cell, Game, GameEvent, GameInput } from './types'

const W = 40
const H = 18
const GROUND = H - 2
const PX = 6
const BOT = [
	['.#.', '###', '#.#'],
	['.#.', '###', '.#.'],
]
const BOT_W = 3
const JUMP_VY = -1.1
const GRAVITY = 0.1
/** Ticks airborne, and the window inside a jump where the bot is more than 3 cells up. */
const AIR_TICKS = (2 * -JUMP_VY) / GRAVITY
const T_CLEAR_LO = 2.2
const T_CLEAR_HI = AIR_TICKS - 2.2

interface Obstacle {
	x: number
	w: number
	h: number
	/** Drones hover at jump height; you run under them, you don't jump into them. */
	flying: boolean
	cleared: boolean
}

/**
 * Endless runner (the bonus stage). The world scrolls at your bandwidth: `setBandwidth`
 * is called when the background speed test finishes and it raises the top speed.
 */
export class Dash implements Game {
	readonly name = 'dash'
	readonly W = W
	readonly H = H
	readonly tickMs = 50
	score = 0
	over = false
	private tick = 0
	private y = GROUND - 2 // top of the 3-tall bot
	private vy = 0
	private grounded = true
	private obstacles: Obstacle[] = []
	private clouds: { x: number; y: number; w: number }[] = [
		{ x: 10, y: 3, w: 5 },
		{ x: 26, y: 6, w: 4 },
		{ x: 38, y: 2, w: 6 },
	]
	private distance = 0
	private turbo = 1
	private nextGap = 18

	/** Bandwidth in Mbps → multiplier on top speed. 100 Mbps ≈ +30%, capped at +60%. */
	setBandwidth(mbps: number): void {
		this.turbo = 1 + Math.min(0.6, mbps / 330)
	}

	private get speed(): number {
		return Math.min(1.1, 0.55 + this.tick * 0.0025) * this.turbo
	}

	step(input: GameInput, demo: boolean): GameEvent[] {
		const events: GameEvent[] = []
		this.tick++
		const speed = this.speed
		const jump = demo ? this.ai(speed) : input.pressed === 'up' || input.fire === true
		if (jump && this.grounded) {
			this.vy = JUMP_VY
			this.grounded = false
			events.push('jump')
		}
		if (!this.grounded) {
			this.vy += GRAVITY
			this.y += this.vy
			if (this.y >= GROUND - 2) {
				this.y = GROUND - 2
				this.vy = 0
				this.grounded = true
			}
		}

		// scroll
		this.distance += speed
		this.score = Math.floor(this.distance) + this.clearedCount * 10
		for (const o of this.obstacles) o.x -= speed
		for (const c of this.clouds) {
			c.x -= speed * 0.25
			if (c.x + c.w < 0) { c.x = W + Math.random() * 10; c.y = 1 + Math.floor(Math.random() * 7) }
		}
		this.obstacles = this.obstacles.filter((o) => o.x + o.w > -1)
		const last = this.obstacles[this.obstacles.length - 1]
		if (!last || W - (last.x + last.w) >= this.nextGap) {
			this.spawn(speed)
		}

		// collisions
		const top = Math.round(this.y)
		for (const o of this.obstacles) {
			const ox = Math.round(o.x)
			const oy = o.flying ? GROUND - 5 : GROUND - o.h
			const overlapX = PX + BOT_W - 1 >= ox && PX <= ox + o.w - 1
			const overlapY = top + 2 >= oy && top <= oy + o.h - 1
			if (overlapX && overlapY) {
				this.over = true
				events.push('die')
				return events
			}
			if (!o.cleared && ox + o.w - 1 < PX) {
				o.cleared = true
				events.push('clear')
			}
		}
		return events
	}

	private get clearedCount(): number {
		return this.obstacles.filter((o) => o.cleared).length + this.clearedGone
	}
	private clearedGone = 0

	private spawn(speed: number): void {
		// remember cleared obstacles that scrolled away so the score doesn't drop
		this.clearedGone += this.obstacles.filter((o) => o.cleared && o.x + o.w <= 0).length
		this.obstacles = this.obstacles.filter((o) => !(o.cleared && o.x + o.w <= 0))
		const flying = this.tick > 200 && Math.random() < 0.25
		const h = flying ? 1 : 1 + Math.floor(Math.random() * 2)
		const w = flying ? 2 : 1 + Math.floor(Math.random() * 2)
		this.obstacles.push({ x: W + 1, w, h, flying, cleared: false })
		// never closer than a full jump, so a landing is always followed by time to jump again
		this.nextGap = Math.round((AIR_TICKS * 1.15 + 4 + Math.random() * 14) * speed)
	}

	cells(): Cell[] {
		const out: Cell[] = []
		for (const c of this.clouds) for (let i = 0; i < c.w; i++) out.push({ x: Math.round(c.x) + i, y: c.y, color: 0x3a1470 })
		for (let x = 0; x < W; x++) out.push({ x, y: H - 1, color: (x + Math.floor(this.distance)) % 4 === 0 ? 0x22f0ff : 0x11506a })
		for (const o of this.obstacles) {
			const ox = Math.round(o.x)
			const oy = o.flying ? GROUND - 5 : GROUND - o.h
			for (let r = 0; r < o.h; r++) for (let c = 0; c < o.w; c++) {
				const x = ox + c
				if (x >= 0 && x < W) out.push({ x, y: oy + r, color: o.flying ? 0x22f0ff : 0xff2bd6 })
			}
		}
		const sprite = BOT[this.grounded ? Math.floor(this.tick / 3) % 2 : 0]
		const top = Math.round(this.y)
		for (let r = 0; r < 3; r++) for (let c = 0; c < BOT_W; c++) if (sprite[r][c] === '#') out.push({ x: PX + c, y: top + r, color: 0xffd84a })
		return out
	}

	/** Demo pilot: jump for ground blocks at the moment the arc will carry the whole bot over them; never for drones. */
	private ai(speed: number): boolean {
		if (!this.grounded) return false
		for (const o of this.obstacles) {
			const gap = o.x - (PX + BOT_W)
			if (gap < -1) continue
			if (o.flying) return false
			const earliest = T_CLEAR_LO * speed
			const latest = T_CLEAR_HI * speed - o.w - BOT_W
			const target = (earliest + latest) / 2
			return gap <= target + speed && gap >= earliest - speed
		}
		return false
	}
}
